import { useEffect, useState } from 'react'
import { fetchVehicleRarity } from './client'
import type { VehicleRarity } from './types'

/**
 * Loads the rarity figures for one plate, once `enabled` turns true.
 *
 * Deliberately not part of `useCarReport`. The API keeps the count on its own endpoint because it
 * takes the RDW around a second and a half — several times the report — and folding the two
 * together here would put the report back behind exactly the wait the split was meant to avoid. The
 * caller passes `enabled: true` once the report is on screen.
 *
 * **A failure is silent.** There is no error state and nothing is shown when the request fails: this
 * is a curiosity at the bottom of the page, and a red panel about a population count would be a
 * worse page than one without the count. The report itself is what the user came for, and it has
 * already rendered by the time this runs.
 */
export function useVehicleRarity(plate: string, enabled: boolean): VehicleRarity | undefined {
  const [loaded, setLoaded] = useState<{ plate: string; rarity: VehicleRarity }>()

  useEffect(() => {
    if (!enabled) {
      return
    }

    const controller = new AbortController()

    fetchVehicleRarity(plate, controller.signal)
      .then((rarity) => setLoaded({ plate, rarity }))
      .catch(() => {
        // Nothing to do and nothing to say — see above.
      })

    return () => controller.abort()
  }, [plate, enabled])

  // The count is keyed on the plate it was fetched for rather than cleared on the way out, because
  // clearing happens in an effect and the render before it would hand the new car the old car's
  // numbers. Wrong figures under the right plate is the one failure this section could produce.
  return loaded?.plate === plate ? loaded.rarity : undefined
}
